import React from 'react';
import PropTypes from 'prop-types';
import { Square, CheckSquare } from 'lucide-react';
import {
  Autocomplete,
  Chip,
  createFilterOptions,
  TextField
} from '@mui/material';
import { cn } from 'lib/utils';

const FILTER_INPUT_RADIUS = '10px';

// sentinel injected as the first autocomplete option to render the "Select All" row
const SELECT_ALL_OPTION = { __selectAll: true };
const defaultFilter = createFilterOptions();

const summaryChipSx = {
  height: 26,
  flexShrink: 0,
  '& .MuiChip-label': {
    paddingLeft: '10px',
    paddingRight: '6px'
  }
};

const filterInputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: FILTER_INPUT_RADIUS,
    backgroundColor: '#fff',
    fontSize: '0.875rem',
    minHeight: 40,
    paddingTop: '6px',
    paddingBottom: '6px',
    paddingLeft: '10px',
    paddingRight: '10px',
    flexWrap: 'nowrap',
    alignItems: 'center',
    gap: '8px',
    overflowX: 'auto',
    overflowY: 'hidden'
  },
  '& .MuiAutocomplete-inputRoot .MuiAutocomplete-input': {
    minWidth: '4.5rem'
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(0, 0, 0, 0.12)'
  },
  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(0, 0, 0, 0.2)'
  },
  '& .MuiInputBase-input::placeholder': {
    opacity: 1,
    color: 'var(--color-grey, #656464)'
  }
};

// chips mode: let selected chips wrap onto multiple lines instead of scrolling
const chipsInputSx = {
  ...filterInputSx,
  '& .MuiOutlinedInput-root': {
    ...filterInputSx['& .MuiOutlinedInput-root'],
    flexWrap: 'wrap',
    overflowX: 'visible',
    overflowY: 'visible',
    gap: '4px'
  },
  '& .MuiAutocomplete-inputRoot .MuiAutocomplete-input': {
    minWidth: '2rem'
  }
};

const tagChipSx = {
  height: 24,
  maxWidth: '100%'
};

const listboxProps = {
  sx: {
    maxHeight: 280,
    overflowY: 'auto',
    overflowX: 'hidden',
    paddingTop: 0,
    paddingBottom: 0,
    WebkitOverflowScrolling: 'touch'
  }
};

/**
 * multi-select autocomplete with a Select All row and checkbox-style options
 * @param options - full option list (the Select All sentinel is injected internally)
 * @param value - currently selected options
 * @param onChange - called with the next selected options array
 * @param placeholder - shown when nothing is selected
 * @param getOptionLabel - option -> display string
 * @param isOptionEqualToValue - option identity comparator
 * @param getOptionSecondaryLabel - optional second line in the dropdown rows
 * @param displayMode - 'count' shows a single summary chip; 'chips' shows one wrapping chip per selection
 * @param className - extra classes for the Autocomplete root
 * @param disabled - disables the input
 */
function FilterAutocomplete({
  options,
  value,
  onChange,
  placeholder,
  getOptionLabel,
  isOptionEqualToValue,
  getOptionSecondaryLabel,
  displayMode = 'count',
  className,
  disabled = false
}) {
  const selectedCount = value.length;
  const allSelected = options.length > 0 && value.length === options.length;
  const autocompleteOptions =
    options.length > 0 ? [SELECT_ALL_OPTION, ...options] : options;
  const isChips = displayMode === 'chips';

  // chips mode renders a deletable chip per selection; count mode suppresses tags
  const renderChipTags = (tagValue, getTagProps) =>
    tagValue.map((option, index) => {
      const { key, ...tagProps } = getTagProps({ index });
      return (
        <Chip
          key={key}
          size='small'
          variant='outlined'
          label={getOptionLabel(option)}
          sx={tagChipSx}
          {...tagProps}
        />
      );
    });

  const renderTags = isChips ? renderChipTags : () => null;
  const inputSx = isChips ? chipsInputSx : filterInputSx;

  return (
    <Autocomplete
      multiple
      disableCloseOnSelect
      disableClearable
      disabled={disabled}
      options={autocompleteOptions}
      value={value}
      onChange={(_, v) => {
        if (v.some(option => option?.__selectAll)) {
          onChange(allSelected ? [] : options);
        } else {
          onChange(v);
        }
      }}
      getOptionLabel={option =>
        option?.__selectAll ? '' : getOptionLabel(option)
      }
      isOptionEqualToValue={(option, val) =>
        option?.__selectAll || val?.__selectAll
          ? false
          : isOptionEqualToValue(option, val)
      }
      filterOptions={(opts, state) => {
        const realOptions = opts.filter(option => !option?.__selectAll);
        const filtered = defaultFilter(realOptions, state);
        return state.inputValue ? filtered : [SELECT_ALL_OPTION, ...filtered];
      }}
      disablePortal
      ListboxProps={listboxProps}
      className={cn('w-full min-w-0', className)}
      renderOption={(props, option, { selected }) => {
        if (option?.__selectAll) {
          return (
            <li
              {...props}
              className={cn(
                'flex items-start gap-2 whitespace-normal border-b border-grey-lighter py-2',
                props.className
              )}
            >
              <span className='mt-px shrink-0 text-grey-dark'>
                {allSelected ? <CheckSquare size={18} /> : <Square size={18} />}
              </span>
              <span className='flex min-w-0 flex-1 flex-col'>
                <span className='wrap-break-word font-medium leading-snug'>
                  Select All
                </span>
              </span>
            </li>
          );
        }

        return (
          <li
            {...props}
            className={cn(
              'flex items-start gap-2 whitespace-normal py-2',
              props.className
            )}
          >
            <span className='mt-px shrink-0 text-grey-dark'>
              {selected ? <CheckSquare size={18} /> : <Square size={18} />}
            </span>
            <span className='flex min-w-0 flex-1 flex-col'>
              <span className='wrap-break-word leading-snug'>
                {getOptionLabel(option)}
              </span>
              {getOptionSecondaryLabel && getOptionSecondaryLabel(option) && (
                <span className='text-xs leading-snug text-grey'>
                  {getOptionSecondaryLabel(option)}
                </span>
              )}
            </span>
          </li>
        );
      }}
      renderTags={renderTags}
      renderInput={params => (
        <TextField
          {...params}
          placeholder={selectedCount > 0 ? '' : placeholder}
          size='small'
          sx={inputSx}
          hiddenLabel
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <>
                {!isChips && selectedCount > 0 ? (
                  <Chip
                    size='small'
                    variant='outlined'
                    label={
                      allSelected ? 'All selected' : `${selectedCount} selected`
                    }
                    onDelete={() => onChange([])}
                    sx={summaryChipSx}
                  />
                ) : null}
                {params.InputProps.startAdornment}
              </>
            )
          }}
        />
      )}
    />
  );
}

FilterAutocomplete.propTypes = {
  options: PropTypes.array.isRequired,
  value: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string.isRequired,
  getOptionLabel: PropTypes.func.isRequired,
  isOptionEqualToValue: PropTypes.func.isRequired,
  getOptionSecondaryLabel: PropTypes.func,
  displayMode: PropTypes.oneOf(['count', 'chips']),
  className: PropTypes.string,
  disabled: PropTypes.bool
};

export default FilterAutocomplete;
